import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { Button } from './button.js';
import { Popover, PopoverContent, PopoverTrigger } from './popover.js';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command.js';

export interface MultiSelectComboboxOption {
  value: string;
  label: string;
}

export interface MultiSelectComboboxProps {
  options: MultiSelectComboboxOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  /** Shown on the trigger when no options are selected. */
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

/**
 * Trigger button + searchable checklist popover for selecting zero or more
 * options. Built from the shared Popover + Command primitives; there is no
 * dedicated multi-select primitive elsewhere in the design system.
 */
export function MultiSelectCombobox({
  options,
  selectedValues,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled = false,
  className,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: MultiSelectComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const selectedSet = React.useMemo(() => new Set(selectedValues), [selectedValues]);

  function toggleValue(value: string) {
    onChange(
      selectedSet.has(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value],
    );
  }

  const triggerLabel =
    selectedValues.length === 0
      ? placeholder
      : options
          .filter((option) => selectedSet.has(option.value))
          .map((option) => option.label)
          .join(', ');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          data-testid={testId}
          className={cn('h-9 w-full justify-between text-[13px] font-normal', className)}
        >
          <span className={cn('truncate', selectedValues.length === 0 && 'text-muted-foreground')}>
            {triggerLabel}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command>
          <CommandInput
            data-testid={testId ? `${testId}-search` : undefined}
            placeholder={searchPlaceholder}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedSet.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    data-testid={testId ? `${testId}-option-${option.value}` : undefined}
                    value={option.label}
                    onSelect={() => toggleValue(option.value)}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary',
                        isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50',
                      )}
                    >
                      {isSelected ? <Check className="h-3 w-3" /> : null}
                    </span>
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
